import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Toast from '../ui/Toast';
import { cn } from '../../utils/cn';

type ToastVariant = 'success' | 'error' | 'info' | 'warning';
type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface ToastOptions {
  id?: string;
  variant?: ToastVariant;
  position?: ToastPosition;
  duration?: number;
}

export interface ToastItem extends ToastOptions {
  id: string;
  title: string;
  message?: string;
  variant: ToastVariant;
  visible: boolean;
  position: ToastPosition;
}

interface ToastContextValue {
  success: (title: string, message?: string, options?: ToastOptions) => string;
  error: (title: string, message?: string, options?: ToastOptions) => string;
  info: (title: string, message?: string, options?: ToastOptions) => string;
  warning: (title: string, message?: string, options?: ToastOptions) => string;
  removeToast: (id: string) => void;
  removeAll: () => void;
}

// Create context
const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook to use the toast functionality
 * @returns Toast methods (success, error, info, warning, removeToast, removeAll)
 */
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Provider component for toast notifications
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Generate a unique ID for each toast
  const generateId = useCallback(
    (): string => `toast-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    []
  );

  // Add a new toast
  const addToast = useCallback((toast: Partial<ToastItem> & { title: string }): string => {
    const id = toast.id || generateId();
    const newToast: ToastItem = {
      ...toast,
      id,
      visible: true,
      variant: toast.variant || 'info',
      position: toast.position || 'bottom-right'
    };
    setToasts(prev => [...prev, newToast]);
    return id;
  }, [generateId]);

  // Remove a toast
  const removeToast = useCallback((id: string): void => {
    setToasts(prev =>
      prev.map(toast =>
        toast.id === id
          ? { ...toast, visible: false }
          : toast
      )
    );

    // After CSS transition finishes, remove from state
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  }, []);

  // Remove all toasts
  const removeAll = useCallback((): void => {
    setToasts(prev => prev.map(toast => ({ ...toast, visible: false })));

    // After CSS transition finishes, clear all
    setTimeout(() => {
      setToasts([]);
    }, 300);
  }, []);

  // Convenience methods for different toast types
  const success = useCallback((title: string, message?: string, options: ToastOptions = {}): string => {
    return addToast({ title, message, variant: 'success', ...options });
  }, [addToast]);

  const error = useCallback((title: string, message?: string, options: ToastOptions = {}): string => {
    return addToast({ title, message, variant: 'error', ...options });
  }, [addToast]);

  const info = useCallback((title: string, message?: string, options: ToastOptions = {}): string => {
    return addToast({ title, message, variant: 'info', ...options });
  }, [addToast]);

  const warning = useCallback((title: string, message?: string, options: ToastOptions = {}): string => {
    return addToast({ title, message, variant: 'warning', ...options });
  }, [addToast]);

  // Group toasts by position
  const groupedToasts = toasts.reduce<Record<string, ToastItem[]>>((acc, toast) => {
    const position: ToastPosition = toast.position;
    if (!acc[position]) acc[position] = [];
    acc[position]!.push(toast);
    return acc;
  }, {});

  // Get position-specific styles
  const getPositionStyles = (position: string): string => {
    const styles: Record<string, string> = {
      'top-left': 'top-4 left-4',
      'top-center': 'top-4 left-1/2 -translate-x-1/2',
      'top-right': 'top-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
      'bottom-right': 'bottom-4 right-4'
    };

    return styles[position] ?? styles['bottom-right'] ?? 'bottom-4 right-4';
  };

  // SSR check - only render portal client-side
  const isBrowser = typeof window !== 'undefined';

  return (
    <ToastContext.Provider
      value={{
        success,
        error,
        info,
        warning,
        removeToast,
        removeAll
      }}
    >
      {children}

      {isBrowser &&
        Object.entries(groupedToasts).map(([position, positionToasts]) =>
          createPortal(
            <div
              className={cn(
                'fixed z-50 flex flex-col gap-2',
                getPositionStyles(position)
              )}
              key={position}
            >
              {positionToasts.map(toast => (
                <Toast
                  key={toast.id}
                  {...toast}
                  onClose={removeToast}
                />
              ))}
            </div>,
            document.body
          )
        )
      }
    </ToastContext.Provider>
  );
};

export default ToastProvider;
